import { useNavigate, useParams } from "react-router-dom";
import HeadingAndSubtitle from "../common/news/HeadingAndSubtitile";
import MainStructureNews from "../common/news/MainStructureNews";
import Papa from "papaparse";
import { useEffect, useState } from "react";
import { NewsItem } from "../common/news/types";
import ReactMarkdown from "react-markdown";
import { blockComponents, inlineComponents } from "../common/news/markdownComponents";
import remarkBreaks from "remark-breaks";
import { Helmet } from "react-helmet-async";
import { isComingSoon } from "../common/news/comingSoonhelper";
import ComingSoon from "../common/news/ComingSoon";

const GradientText = ({ children }: { children: React.ReactNode }) => (
    <span
        className="text-xs uppercase tracking-widest font-bold"
        style={{
            background: "linear-gradient(90deg, #00858F 0%, #E6007E 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
        }}
    >
    {children}
  </span>
);

export default function NewsDetail() {
    const { name, id } = useParams<{ name: string; id: string }>();
    const [newsItem, setNewsItem] = useState<NewsItem | null>(null);
    const [filteredNews, setFilteredNews] = useState<NewsItem[]>([]);
    const [seeMore, setSeeMore] = useState(false);
    const navigate = useNavigate();

    const handleClick = (item: NewsItem) => {
        if (item.link) {
            window.open(item.link, "_blank", "noopener,noreferrer");
        } else {
            navigate(`/news/${item.category}/${item.news_id}`, {
                state: { NewItem: item, filteredNews },
            });
            window.scrollTo(0, 0);
        }
    };

    useEffect(() => {
        const fetchNewsItem = async () => {
            const response = await fetch("/csv/news_content.csv");
            const csvText = await response.text();
            Papa.parse(csvText, {
                header: true,
                complete: (result) => {
                    const newsData = result.data as NewsItem[];
                    const item = newsData.find((news) => news.news_id === id);
                    setNewsItem(item || null);
                    setFilteredNews(newsData.filter((news) => news.category === name));
                },
            });
        };
        fetchNewsItem();
        window.scrollTo(0, 0);
    }, [name, id]);

    if (!newsItem) {
        return (
            <div className="flex items-center justify-center h-screen text-white uppercase tracking-widest text-sm">
                Loading...
            </div>
        );
    }

    // Se la news è in coming soon, mostra il countdown al posto del contenuto.
    if (isComingSoon(newsItem)) {
        return (
            <ComingSoon
                counterDate={newsItem.counter_date}
                title={newsItem.titleNews}
            />
        );
    }

    const relatedNews = filteredNews.filter((n) => n.news_id !== newsItem.news_id);

    return (
        <div className="overflow-x-hidden">
            <Helmet>
                <title>{newsItem?.titleNews || "News"}</title>
                <meta name="description" content={newsItem?.subtitle || "Revolvspace"} />
            </Helmet>

            {/* ── HEADER ── */}
            <div className="pt-32 pb-10 px-6 md:px-24 lg:px-36">
                <div className="mb-5">
                    <GradientText>
                        {newsItem.category}
                        {newsItem.publication_date ? ` · ${newsItem.publication_date}` : ""}
                        {newsItem.reading_time ? ` · ${newsItem.reading_time} min read` : ""}
                        {newsItem.author_name ? ` · ${newsItem.author_name}` : ""}
                    </GradientText>
                </div>
                <h1 className="text-white font-bold text-3xl md:text-5xl uppercase leading-tight mb-0 md:max-w-6xl">
                    <ReactMarkdown remarkPlugins={[remarkBreaks]} components={inlineComponents}>
                        {newsItem?.titleNews?.replace(/\\n/g, "\n")}
                    </ReactMarkdown>
                </h1>
            </div>

            {/* ── HERO IMAGE ── */}
            <div className="px-6 md:px-24 lg:px-36 mb-16">
                <img
                    src={"/IMAGES/" + newsItem.news_id + "/big.png"}
                    className="w-full max-h-[55rem] object-contain"
                    alt={newsItem.titleNews}
                />
            </div>

            {/* ── INTRO ── */}
            {(newsItem?.heading || newsItem?.subtitle) && (
                <div className="px-6 md:px-24 lg:px-80 mb-14">
                    <HeadingAndSubtitle
                        heading={newsItem?.heading}
                        subtitle={newsItem?.subtitle}
                    />
                </div>
            )}

            {/* ── MAIN QUOTE ── */}
            {newsItem?.quote1 && (
                <div className="px-6 md:px-24 lg:px-80 mb-14">
                    <div
                        className="border-l-2 pl-8"
                        style={{ borderImage: "linear-gradient(180deg, #00858F 0%, #E6007E 100%) 1" }}
                    >
                        {/* <div> e non <p>: la citazione può contenere un elenco. */}
                        <div className="text-white text-xl md:text-2xl italic leading-relaxed opacity-90">
                            <ReactMarkdown remarkPlugins={[remarkBreaks]} components={blockComponents}>
                                {newsItem?.quote1?.replace(/\\n/g, "\n")}
                            </ReactMarkdown>
                        </div>
                        {newsItem?.quote1_names && (
                            <div className="mt-3">
                                <GradientText>{newsItem.quote1_names}</GradientText>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── MAIN BODY ── */}
            <div className="px-6 md:px-24 lg:px-80 mb-16">
                <MainStructureNews
                    heading_1={newsItem?.heading_1}
                    subtitle_1={newsItem?.subtitle_1}
                    quote_1={newsItem?.quote_1}
                    quote_author_1={newsItem?.quote_author_1}
                    image_1={newsItem?.image_1 ? "/IMAGES/" + newsItem?.news_id + "/image_1.png" : ""}
                    image_caption_1={newsItem?.image_caption_1}
                    heading_2={newsItem?.heading_2}
                    subtitle_2={newsItem?.subtitle_2}
                    quote_2={newsItem?.quote_2}
                    quote_author_2={newsItem?.quote_author_2}
                    image_2={newsItem?.image_2 ? "/IMAGES/" + newsItem?.news_id + "/image_2.png" : ""}
                    image_caption_2={newsItem?.image_caption_2}
                    heading_3={newsItem?.heading_3}
                    subtitle_3={newsItem?.subtitle_3}
                    quote_3={newsItem?.quote_3}
                    quote_author_3={newsItem?.quote_author_3}
                    image_3={newsItem?.image_3 ? "/IMAGES/" + newsItem?.news_id + "/image_3.png" : ""}
                    image_caption_3={newsItem?.image_caption_3}
                    heading_4={newsItem?.heading_4}
                    subtitle_4={newsItem?.subtitle_4}
                    quote_4={newsItem?.quote_4}
                    quote_author_4={newsItem?.quote_author_4}
                    image_4={newsItem?.image_4 ? "/IMAGES/" + newsItem?.news_id + "/image_4.png" : ""}
                    image_caption_4={newsItem?.image_caption_4}
                    heading_5={newsItem?.heading_5}
                    subtitle_5={newsItem?.subtitle_5}
                    quote_5={newsItem?.quote_5}
                    quote_author_5={newsItem?.quote_author_5}
                    image_5={newsItem?.image_5 ? "/IMAGES/" + newsItem?.news_id + "/image_5.png" : ""}
                    image_caption_5={newsItem?.image_caption_5}
                />
            </div>

            {/* ── EXTRA PARAGRAPHS ── */}
            <div className="px-6 md:px-24 lg:px-80 mb-24 space-y-10">
                <HeadingAndSubtitle
                    heading={newsItem?.header_paragraph_1}
                    subtitle={newsItem?.description_paragraph_1}
                />
                <HeadingAndSubtitle
                    heading={newsItem?.header_paragraph_2}
                    subtitle={newsItem?.description_paragraph_2}
                />
                <HeadingAndSubtitle
                    heading={newsItem?.header_paragraph_3}
                    subtitle={newsItem?.description_paragraph_3}
                />
            </div>

            {/* ── DISCOVER MORE ── */}
            {newsItem.additional_link && (
                <a href={newsItem.additional_link} target="_blank" rel="noopener noreferrer">
                    <div className="flex items-center justify-center pb-16">
                        <button
                            className="rounded-full w-auto h-12 text-center text-white px-8 font-bold transform transition-transform duration-200 hover:scale-110"
                            style={{
                                background: "linear-gradient(90deg, #00858F 0%, #E6007E 100%)",
                            }}
                        >
                            DISCOVER MORE
                        </button>
                    </div>
                </a>
            )}

            {/* ── RELATED NEWS ── */}
            {relatedNews.length > 0 && (
                <div className="pb-24">
                    <div className="bg-gradient-to-r from-gradient-start to-gradient-end h-px mx-6 md:mx-16 mb-16" />

                    <h2 className="text-4xl md:text-5xl font-bold text-white uppercase text-center mb-14 px-6">
                        Related News
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 px-6 md:px-16">
                        {(seeMore ? relatedNews : relatedNews.slice(0, 3)).map((item, index) => (
                            <div
                                key={index}
                                className="cursor-pointer group"
                                data-aos="fade-up"
                                data-aos-offset="80"
                                data-aos-delay={String((index % 3) * 80)}
                                data-aos-duration="500"
                                data-aos-once="true"
                                onClick={() => handleClick(item)}
                            >
                                <div className="overflow-hidden">
                                    <img
                                        src={"/IMAGES/" + item.news_id + "/small.png"}
                                        alt={item.titleNews}
                                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                </div>
                                <div className="pt-4 space-y-2">
                                    <GradientText>
                                        {item.category} · {item.publication_date}
                                    </GradientText>
                                    <h3 className="text-white font-bold uppercase text-sm md:text-base leading-snug line-clamp-2 group-hover:opacity-70 transition-opacity">
                                        {item.titleNews?.replace(/\\n/g, " ")}
                                    </h3>
                                    <p className="text-white text-opacity-50 text-sm leading-relaxed line-clamp-2">
                                        {item.subtitle?.replace(/\\n/g, " ")}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>




                    {relatedNews.length > 3 && !seeMore && (
                        <div className="flex justify-center mt-14">
                            <button
                                onClick={() => setSeeMore(true)}
                                className="rounded-full text-white font-bold py-3 px-10 text-sm uppercase tracking-widest transform transition-transform duration-200 hover:scale-110"
                                style={{ background: "linear-gradient(90deg, #00858F 0%, #E6007E 100%)" }}
                            >
                                See More
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}